import { getApiBaseUrl } from '../../salon/utils/apiConfig';
import { isGuestCheckInFlow, isPassportFlow, resolveFaceApiPrefix } from './faceIdentityRoutes';
import { fetchWithTimeout } from '../../salon/utils/fetchWithTimeout';
import {
  getFranchiseCode,
  getFranchiseId,
  getFranchiseOrganizationId,
  getFranchiseStylistId,
  franchiseAuthHeaders,
} from '../../shared/franchiseFlowAuth';
import {
  getGuestFranchiseId,
  getGuestOrganizationId,
  guestAuthHeaders,
  hasGuestFlowAuth,
} from '../../shared/guestFlowAuth';
import {
  appendDeviceLocationToFormData,
  acquireKioskDeviceLocation,
  getDeviceLocationHeaders,
  readCachedDeviceLocation,
} from '../../shared/deviceLocationCache';
import { readFranchiseFlowAuth } from '../../shared/franchiseFlowAuth';
import { clearSalonGuestPii, purgeLegacySalonPiiSessionKeys } from '../../salon/utils/salonGuestPiiStore';
import { clearSalonFaceSession } from '../../salon/utils/salonFaceSession';
import { isSalonDeskMode } from '../../salon/utils/salonKioskMode';
const DEFAULT_ORG = 'NATURALS-HQ';
export const FACE_PROFILE_STORAGE_KEY = 'enterprise_nxt_face_profile';

function apiBase() {
  return (getApiBaseUrl() || process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
}

function faceApiPrefix() {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  return resolveFaceApiPrefix(pathname);
}

function onGuestCheckInPath() {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  return isGuestCheckInFlow(pathname);
}

function authHeaders(extra = {}) {
  // Guest JWT must not override the Passport kiosk franchise session.
  if (onGuestCheckInPath() && hasGuestFlowAuth()) return guestAuthHeaders(extra);
  return franchiseAuthHeaders(extra);
}

function isPassportKioskDevicePath() {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  return isPassportFlow(pathname) && !isGuestCheckInFlow(pathname);
}

function isKioskGeofenceEnforced() {
  if (isSalonDeskMode()) return false;
  const session = readFranchiseFlowAuth();
  if (onGuestCheckInPath() && hasGuestFlowAuth()) return true;
  return session?.geofence_enabled === true;
}

async function profileRequestHeaders(extra = {}) {
  const base = authHeaders(extra);
  if (!isKioskGeofenceEnforced()) return base;
  const cached = readCachedDeviceLocation();
  if (cached) return getDeviceLocationHeaders(base);
  try {
    const loc = await acquireKioskDeviceLocation({ required: false });
    if (loc) return getDeviceLocationHeaders(base);
  } catch {
    /* server may still accept cached store check */
  }
  return base;
}

/**
 * HQ organization id for face enroll/match (shared across all franchises).
 * Prefer franchise-flow login, then explicit / URL; default HQ.
 */
export function resolveFaceOrganizationId(explicit) {
  const fromArg = String(explicit || '').trim();
  if (fromArg) return fromArg;
  const fromFranchise = getFranchiseOrganizationId();
  if (fromFranchise) return fromFranchise;
  const fromGuest = getGuestOrganizationId();
  if (fromGuest) return fromGuest;
  try {
    if (typeof window !== 'undefined') {
      const fromQuery = new URLSearchParams(window.location.search || '')
        .get('organization_id')
        ?.trim();
      if (fromQuery) return fromQuery;
    }
  } catch (_) {
    /* ignore */
  }
  return DEFAULT_ORG;
}

export function resolveFranchiseStylistId(explicit) {
  if (explicit != null && String(explicit).trim() !== '') return explicit;
  return getFranchiseStylistId();
}

/**
 * Franchise used for last-visit location (area/city/country), not face matching.
 */
export function resolveFaceVisitOrganizationId(explicit) {
  const fromArg = String(explicit || '').trim();
  if (fromArg) return fromArg;
  const fromOutlet = getFranchiseId() || getFranchiseCode();
  if (fromOutlet) return fromOutlet;
  const fromGuest = getGuestFranchiseId();
  if (fromGuest) return fromGuest;
  try {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search || '');
      const fromQuery = (
        params.get('visit_organization_id') ||
        params.get('franchise_id') ||
        params.get('franchise_code') ||
        ''
      ).trim();
      if (fromQuery) return fromQuery;
    }
  } catch (_) {
    /* ignore */
  }
  return '';
}

/**
 * @param {Blob|File} imageBlob
 * @param {{ organizationId?: string }} [opts]
 */
export async function recognizeSalonFace(imageBlob, opts = {}) {
  const geoRequired = isKioskGeofenceEnforced();
  const passportKiosk = isPassportKioskDevicePath();
  let loc = readCachedDeviceLocation();
  const needDeviceCoords = geoRequired || passportKiosk;
  if (needDeviceCoords && !loc) {
    loc = await acquireKioskDeviceLocation({ required: geoRequired });
  }
  if (geoRequired && !loc) {
    const err = new Error(
      'Location not detected. Allow GPS access on this device, then try again.',
    );
    err.code = 'DEVICE_LOCATION_REQUIRED';
    throw err;
  }
  const fd = new FormData();
  fd.append('image', imageBlob, 'capture.jpg');
  fd.append('organization_id', resolveFaceOrganizationId(opts.organizationId));
  const visitOrg = resolveFaceVisitOrganizationId(opts.visitOrganizationId);
  if (visitOrg) fd.append('visit_organization_id', visitOrg);
  if (loc) appendDeviceLocationToFormData(fd);
  const headers = loc ? getDeviceLocationHeaders(authHeaders()) : authHeaders();
  const res = await fetchWithTimeout(`${apiBase()}${faceApiPrefix()}/recognize`, {
    method: 'POST',
    headers,
    body: fd,
    // CPU cold start can be slow; server also hard-times out inference.
    timeoutMs: 120000,
  });
  const data = await res.json().catch(() => ({}));
  const soft = new Set([
    'unknown',
    'no_face',
    'multiple_faces',
    'low_quality',
    'face_too_small',
    'blurry_image',
    'under_exposed',
    'over_exposed',
    'image_too_small',
    'near_match',
    'ambiguous',
    'confirm_match',
  ]);
  if (!res.ok && !soft.has(String(data?.status || '').toLowerCase())) {
    const code = String(data?.code || '').toUpperCase();
    const locationMsg =
      code === 'DEVICE_LOCATION_REQUIRED'
        ? 'Device location is required. Allow GPS access and try again at the store.'
        : code === 'STORE_LOCATION_REQUIRED'
          ? 'Store location is not configured for this outlet. Contact your administrator.'
          : code === 'OUTSIDE_GEOFENCE'
            ? data?.error || data?.message
            : null;
    const err = new Error(
      locationMsg || data?.message || data?.error || `Recognize failed (${res.status})`,
    );
    err.code = code || undefined;
    err.payload = data;
    throw err;
  }
  return data;
}

/**
 * Guest confirmed a familiar profile from confirm_match candidates.
 * @param {string} userId
 * @param {Blob|File} imageBlob
 */
export async function confirmSalonFaceMatch(userId, imageBlob, opts = {}) {
  const fd = new FormData();
  fd.append('image', imageBlob, 'capture.jpg');
  fd.append('user_id', String(userId || '').trim());
  fd.append('organization_id', resolveFaceOrganizationId(opts.organizationId));
  const visitOrg = resolveFaceVisitOrganizationId(opts.visitOrganizationId);
  if (visitOrg) fd.append('visit_organization_id', visitOrg);
  appendDeviceLocationToFormData(fd);
  const res = await fetchWithTimeout(`${apiBase()}${faceApiPrefix()}/confirm-match`, {
    method: 'POST',
    headers: authHeaders(),
    body: fd,
    timeoutMs: 120000,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    const err = new Error(data?.message || data?.error || `Confirm failed (${res.status})`);
    err.code = data?.code;
    err.payload = data;
    throw err;
  }
  return data;
}

/**
 * Look up an enrolled face profile by mobile (skip camera).
 * @param {string} mobile
 * @param {{ organizationId?: string }} [opts]
 */
export async function registerSalonProfile(profile, opts = {}) {
  const body = {
    name: profile.name || profile.full_name || '',
    mobile: profile.mobile || profile.mobileNumber || '',
    organization_id: resolveFaceOrganizationId(opts.organizationId),
    email: profile.email || undefined,
    gender: profile.gender || undefined,
    skin_type: profile.skinType || profile.skin_type || undefined,
    date_of_birth: profile.dateOfBirth || profile.date_of_birth || undefined,
  };
  const visitOrg = resolveFaceVisitOrganizationId(opts.visitOrganizationId);
  if (visitOrg) body.visit_organization_id = visitOrg;
  // Another person on a number that already has a profile (family member booked on the same mobile).
  if (opts.newPerson) body.new_person = true;
  const res = await fetchWithTimeout(`${apiBase()}${faceApiPrefix()}/register-profile`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    timeoutMs: 60000,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 409 || data?.code === 'AMBIGUOUS') {
    const err = new Error(data?.message || 'Multiple profiles match this mobile number');
    err.code = 'AMBIGUOUS';
    err.candidates = Array.isArray(data?.candidates) ? data.candidates : [];
    err.payload = data;
    throw err;
  }
  if (!res.ok || !data?.success) {
    const err = new Error(data?.message || data?.error || `Registration failed (${res.status})`);
    err.code = data?.code;
    err.payload = data;
    throw err;
  }
  return data;
}

export async function lookupFaceUserByMobile(mobile, opts = {}) {
  const body = {
    mobile: String(mobile || '').trim(),
    organization_id: resolveFaceOrganizationId(opts.organizationId),
  };
  const visitOrg = resolveFaceVisitOrganizationId(opts.visitOrganizationId);
  if (visitOrg) body.visit_organization_id = visitOrg;

  const res = await fetchWithTimeout(`${apiBase()}${faceApiPrefix()}/lookup-mobile`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
    timeoutMs: 20000,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 404 || data?.status === 'not_found') {
    const err = new Error(data?.message || 'No profile found for this mobile number');
    err.code = 'NOT_FOUND';
    err.payload = data;
    throw err;
  }
  if (res.status === 409 || data?.status === 'ambiguous' || data?.code === 'AMBIGUOUS') {
    const err = new Error(data?.message || 'Multiple profiles match this mobile number');
    err.code = 'AMBIGUOUS';
    err.candidates = Array.isArray(data?.candidates) ? data.candidates : [];
    err.payload = data;
    throw err;
  }
  if (!res.ok || !data?.success || !data?.user) {
    const err = new Error(data?.message || data?.error || `Lookup failed (${res.status})`);
    err.payload = data;
    throw err;
  }
  return data;
}

/** Warm InsightFace in the worker before the user captures (avoids first-request hang). */
export async function warmSalonFaceEngine(apiPrefix) {
  const prefix =
    typeof apiPrefix === 'string' && apiPrefix.startsWith('/')
      ? apiPrefix
      : faceApiPrefix();
  const res = await fetchWithTimeout(`${apiBase()}${prefix}/ready?warm=1`, {
    method: 'GET',
    headers: authHeaders(),
    timeoutMs: 120000,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.message || data?.error || `Face engine not ready (${res.status})`);
    err.payload = data;
    throw err;
  }
  return data;
}

/**
 * @param {Blob|File} imageBlob
 * @param {object} profile
 * @param {{ organizationId?: string }} [opts]
 */
/** Minimal JPEG for stylist-led profile create (no camera). */
export async function stylistRegisterPlaceholderBlob() {
  const dataUrl =
    'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wCEAAkGBxISEhUQEhIVFhUVFRUYFxgYFRgYFxgWGBgYGBgYGBgYHSggGBolHRcVITEhJSkrLi4uFx8zODMsNygtLisBCgoKDg0OGxAQGy0lHyUtLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLf/AABEIAAEAAQMBIgACEQEDEQH/xAAbAAACAwEBAQAAAAAAAAAAAAADBAECBQYAB//EAD0QAAIBAwMCBAMFBgUDBQEAAAECAwAEERIhMQVBUQYTImFxFDKBkaGxwdEjQlLh8PEFM2KCkvEzU7L/xAAZAQADAQEBAAAAAAAAAAAAAAABAgMABAX/xAAhEQACAwEBAQEBAQEBAAAAAAAAAQIRAxIhBDFBE1FhInH/2gAMAwEAAhEDEQA/APQKKKKKAP/Z';
  return dataUrlToBlob(dataUrl);
}

export async function dataUrlToBlob(dataUrl) {
  const text = String(dataUrl || '').trim();
  if (!text) throw new Error('No face capture available — please scan again');
  if (text.startsWith('data:')) {
    const comma = text.indexOf(',');
    if (comma < 0) throw new Error('Invalid face capture image');
    const header = text.slice(0, comma);
    const b64 = text.slice(comma + 1);
    const mimeMatch = /^data:([^;,]+)/i.exec(header);
    const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mime });
  }
  const res = await fetch(text);
  return res.blob();
}

export async function registerSalonFace(imageBlob, profile, opts = {}) {
  const skipDeviceLocation =
    Boolean(opts.skipDeviceLocation) || !isKioskGeofenceEnforced();
  if (!skipDeviceLocation) {
    let loc = readCachedDeviceLocation();
    if (!loc) {
      loc = await acquireKioskDeviceLocation();
    }
    if (!loc) {
      throw new Error('Location not detected. Allow GPS access on this device, then try again.');
    }
  }
  const fd = new FormData();
  fd.append('image', imageBlob, 'capture.jpg');
  fd.append('organization_id', resolveFaceOrganizationId(opts.organizationId));
  const visitOrg = resolveFaceVisitOrganizationId(opts.visitOrganizationId);
  if (visitOrg) fd.append('visit_organization_id', visitOrg);
  fd.append('name', profile.name || '');
  fd.append('mobile', profile.mobile || profile.mobileNumber || '');
  if (profile.email) fd.append('email', profile.email);
  if (profile.gender) fd.append('gender', profile.gender);
  if (profile.age != null && profile.age !== '') fd.append('age', String(profile.age));
  if (profile.dateOfBirth || profile.date_of_birth) {
    fd.append('date_of_birth', profile.dateOfBirth || profile.date_of_birth);
  }
  if (profile.skinType || profile.skin_type) {
    fd.append('skin_type', profile.skinType || profile.skin_type);
  }
  if (profile.membershipTier || profile.membership_tier) {
    fd.append('membership_tier', profile.membershipTier || profile.membership_tier);
  }
  if (profile.memberId || profile.member_id) {
    fd.append('member_id', profile.memberId || profile.member_id);
  }
  if (profile.membershipPoints != null && profile.membershipPoints !== '') {
    fd.append('membership_points', String(profile.membershipPoints));
  }
  if (profile.allergies != null) {
    const allergies = Array.isArray(profile.allergies)
      ? profile.allergies
      : String(profile.allergies)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
    fd.append('allergies', JSON.stringify(allergies));
  }
  if (profile.preferenceNotes) fd.append('preference_notes', profile.preferenceNotes);
  if (profile.anniversaryDate || profile.anniversary_date) {
    fd.append('anniversary_date', profile.anniversaryDate || profile.anniversary_date);
  }
  if (profile.preferences && typeof profile.preferences === 'object') {
    fd.append('preferences', JSON.stringify(profile.preferences));
  }
  fd.append('consent', 'true');
  fd.append('consent_version', profile.consentVersion || profile.consent_version || 'kiosk-v1');
  if (opts.stylistLed) {
    fd.append('stylist_led', '1');
  }
  if (!skipDeviceLocation) {
    appendDeviceLocationToFormData(fd);
  }
  const prefix = String(opts.faceApiPrefix || faceApiPrefix()).replace(/\/$/, '');
  const headerBuilder = opts.buildAuthHeaders || authHeaders;
  const res = await fetchWithTimeout(`${apiBase()}${prefix}/register`, {
    method: 'POST',
    headers: headerBuilder(),
    body: fd,
    timeoutMs: opts.timeoutMs ?? 45000,
  });
  const data = await res.json().catch(() => ({}));
  // Existing profile recovered during register (face or mobile dedupe).
  if (res.ok && data?.success && data?.user && ['existing_face', 'existing_mobile', 'found'].includes(String(data?.status || ''))) {
    return data;
  }
  if (!res.ok || !data?.success) {
    throw new Error(data?.message || data?.error || `Register failed (${res.status})`);
  }
  return data;
}

export async function getFaceUserProfile(faceUserId) {
  if (!faceUserId) return null;
  const headers = await profileRequestHeaders();
  const res = await fetchWithTimeout(
    `${apiBase()}${faceApiPrefix()}/users/${encodeURIComponent(faceUserId)}/profile`,
    { method: 'GET', headers, timeoutMs: 15000 },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.message || data?.error || 'Failed to load profile');
  }
  return data;
}

export async function updateFaceUserProfile(faceUserId, payload) {
  if (!faceUserId) return null;
  if (isKioskGeofenceEnforced()) {
    const cached = readCachedDeviceLocation();
    if (!cached) {
      try {
        await acquireKioskDeviceLocation({ required: true });
      } catch {
        const err = new Error(
          'Location not detected. Allow GPS access on this device, then try again.',
        );
        err.code = 'DEVICE_LOCATION_REQUIRED';
        throw err;
      }
    }
  }
  const headers = await profileRequestHeaders({ 'Content-Type': 'application/json' });
  const res = await fetchWithTimeout(
    `${apiBase()}${faceApiPrefix()}/users/${encodeURIComponent(faceUserId)}/profile`,
    {
      method: 'PUT',
      headers,
      body: JSON.stringify(payload || {}),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.message || data?.error || 'Failed to update profile');
  }
  return data;
}

export async function checkinFaceUser(faceUserId, payload = {}) {
  if (!faceUserId) return null;
  const body = {
    ...(payload || {}),
    organization_id:
      payload.organization_id ||
      payload.organizationId ||
      resolveFaceOrganizationId(),
    visit_organization_id:
      payload.visit_organization_id ||
      payload.visitOrganizationId ||
      resolveFaceVisitOrganizationId() ||
      undefined,
  };
  const res = await fetchWithTimeout(
    `${apiBase()}${faceApiPrefix()}/users/${encodeURIComponent(faceUserId)}/checkin`,
    {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.message || data?.error || 'Failed to check in');
  }
  return data;
}

/**
 * Previous treatments / products purchased for a face user.
 * @param {string} faceUserId
 * @param {{ limit?: number }} [opts]
 */
export async function fetchSalonPurchaseHistory(faceUserId, opts = {}) {
  if (!faceUserId) return { treatments: [], products: [] };
  const limit = opts.limit != null ? Number(opts.limit) : 20;
  const qs = Number.isFinite(limit) ? `?limit=${encodeURIComponent(String(limit))}` : '';
  const res = await fetchWithTimeout(
    `${apiBase()}${faceApiPrefix()}/users/${encodeURIComponent(faceUserId)}/purchase-history${qs}`,
    { method: 'GET', headers: authHeaders(), timeoutMs: 15000 },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.message || data?.error || 'Failed to load purchase history');
  }
  return {
    treatments: Array.isArray(data.treatments) ? data.treatments : [],
    products: Array.isArray(data.products) ? data.products : [],
  };
}

/**
 * Persist last_salon_reference on the face user after session creation.
 */
export async function updateFaceUserSalonReference(faceUserId, salonReference) {
  if (!faceUserId || !salonReference) return null;
  const res = await fetchWithTimeout(
    `${apiBase()}${faceApiPrefix()}/users/${encodeURIComponent(faceUserId)}/salon-reference`,
    {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ salon_reference: salonReference }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.message || data?.error || 'Failed to update salon reference');
  }
  return data;
}

let _faceProfileSnapshot = null;

export function persistFaceProfileSnapshot(snapshot) {
  const safe = { ...(snapshot || {}) };
  delete safe.image;
  if (safe.profile && typeof safe.profile === 'object') {
    safe.profile = { ...safe.profile };
    delete safe.profile.image;
  }
  _faceProfileSnapshot = safe;
  try {
    sessionStorage.removeItem(FACE_PROFILE_STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
}

export function readFaceProfileSnapshot() {
  return _faceProfileSnapshot ? { ..._faceProfileSnapshot } : null;
}

export function clearFaceProfileSnapshot() {
  _faceProfileSnapshot = null;
  try {
    sessionStorage.removeItem(FACE_PROFILE_STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
}

export const FACE_CAPTURE_STORAGE_KEY = 'enterprise_nxt_face_capture';
export const FACE_PENDING_STORAGE_KEY = 'enterprise_nxt_face_pending';

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Failed to read capture'));
    reader.readAsDataURL(blob);
  });
}

let _faceCaptureDataUrl = null;

export async function persistFaceCapture(blob) {
  if (!blob) return null;
  const dataUrl = await blobToDataUrl(blob);
  _faceCaptureDataUrl = dataUrl;
  try {
    sessionStorage.removeItem(FACE_CAPTURE_STORAGE_KEY);
  } catch (_) {
    // ignore quota
  }
  return dataUrl;
}

export function clearFaceCapture() {
  _faceCaptureDataUrl = null;
  try {
    sessionStorage.removeItem(FACE_CAPTURE_STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
}

export function readFaceCaptureDataUrl() {
  return _faceCaptureDataUrl;
}

export function readFaceCaptureBlob() {
  const dataUrl = readFaceCaptureDataUrl();
  if (!dataUrl || !dataUrl.includes(',')) return null;
  try {
    const [meta, b64] = dataUrl.split(',');
    const mime = (meta.match(/data:(.*?);/) || [])[1] || 'image/jpeg';
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  } catch (_) {
    return null;
  }
}

let _facePending = null;

export function persistFacePending(payload) {
  _facePending = payload || {};
  try {
    sessionStorage.removeItem(FACE_PENDING_STORAGE_KEY);
  } catch (_) {
    // ignore
  }
}

export function readFacePending() {
  return _facePending;
}

export function clearFacePending() {
  _facePending = null;
  clearFaceCapture();
  try {
    sessionStorage.removeItem(FACE_PENDING_STORAGE_KEY);
    sessionStorage.removeItem(FACE_CAPTURE_STORAGE_KEY);
  } catch (_) {
    // ignore
  }
}

/** Clear all guest face/salon identity leftovers (logout / re-login). */
export function clearAllGuestKioskState() {
  clearFaceProfileSnapshot();
  clearFacePending();
  clearFaceCapture();
  clearSalonGuestPii();
  clearSalonFaceSession();
  purgeLegacySalonPiiSessionKeys();
}

/** @param {string} dob YYYY-MM-DD */
export function ageFromDateOfBirth(dob) {
  if (!dob) return null;
  const d = new Date(`${dob}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  if (age < 0 || age > 120) return null;
  return age;
}

export function createPassportReferenceToken() {
  return `passport-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function deskQueueSource(kind) {
  // Guest check-in QR is path-scoped so a leftover guest JWT cannot retag Passport.
  const fromGuestQr = onGuestCheckInPath() && hasGuestFlowAuth();
  if (kind === 'pending') {
    return fromGuestQr ? 'guest_qr_pending_registration' : 'passport_pending_registration';
  }
  return fromGuestQr ? 'guest_qr' : 'passport_face';
}

/** Queue a new Passport / Guest QR guest for partner front-desk registration. */
export async function submitPassportPendingRegistration({ captureUrl, referenceToken }) {
  const { createPartnerCheckinQueue } = await import('../../salon-admin/utils/checkinQueueApi');
  const token = String(referenceToken || createPassportReferenceToken()).trim();
  const source = deskQueueSource('pending');
  const checkin = await createPartnerCheckinQueue({
    customerName: 'New guest',
    source,
    referenceToken: token,
    forceNew: true,
    customerSnapshot: {
      registration_status: 'pending',
      face_capture: captureUrl,
      captureUrl,
      reference_token: token,
      entry_source: source,
      pending_at: new Date().toISOString(),
    },
  });
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('checkin-queue-updated'));
  }
  return { checkin, referenceToken: token };
}

function notifyCheckinQueueUpdated() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('checkin-queue-updated'));
  }
}

/** Recognized returning guest → partner check-in queue + visit record. */
export async function submitPassportReturningCheckin({ user, profile, captureUrl, isNewMember = false }) {
  if (!user?.id) throw new Error('Guest profile is missing');
  const { createPartnerCheckinQueue } = await import('../../salon-admin/utils/checkinQueueApi');
  const referenceToken = createPassportReferenceToken();
  const source = deskQueueSource('face');
  const display = String(user.name || user.full_name || 'Guest').trim() || 'Guest';
  const mobile = String(user.mobile || user.phone || '').trim();
  const faceOrg =
    user.organization_id ||
    user.organizationId ||
    resolveFaceOrganizationId() ||
    getFranchiseOrganizationId() ||
    '';
  const prefs = profile?.preferences && typeof profile.preferences === 'object' ? profile.preferences : {};
  const faceDob =
    prefs.date_of_birth || prefs.dob || user.date_of_birth || user.dob || '';
  const faceAge = user.age != null && String(user.age).trim() !== '' ? user.age : '';

  try {
    await checkinFaceUser(user.id, {
      notes: isNewMember
        ? (source.startsWith('guest_qr') ? 'guest_qr_welcome_new' : 'passport_welcome_new')
        : (source.startsWith('guest_qr') ? 'guest_qr_returning' : 'passport_returning'),
      services_summary: { selected_services: [], service_names: [] },
      organization_id: faceOrg,
    });
  } catch {
    /* non-blocking */
  }

  const checkin = await createPartnerCheckinQueue({
    faceUserId: user.id,
    customerName: display,
    mobile,
    organizationId: faceOrg,
    source,
    referenceToken,
    startService: false,
    customerSnapshot: {
      name: display,
      full_name: display,
      mobile,
      email: user.email || '',
      gender: user.gender || '',
      age: faceAge,
      date_of_birth: faceDob,
      face_user_id: user.id,
      organization_id: faceOrg,
      reference_token: referenceToken,
      entry_source: source,
      profile,
      is_new_member: Boolean(isNewMember),
      face_capture: captureUrl,
      captureUrl,
      passport_checked_in_at: new Date().toISOString(),
    },
  });
  notifyCheckinQueueUpdated();
  const reused = Boolean(checkin?.reused_open_visit);
  return { checkin, referenceToken, reused };
}

/** User-facing copy when today's open visit already exists. */
export function passportDuplicateVisitMessage(checkin) {
  const code = String(checkin?.customer_code || '').trim();
  const status = String(checkin?.status || 'active').toLowerCase();
  const statusLine =
    status === 'in_service'
      ? 'Your visit is already in service.'
      : 'You are already on today’s guest list.';
  if (code) {
    return `${statusLine} Check-in code: ${code}. Please see the front desk if you need anything.`;
  }
  return `${statusLine} Please see the front desk if you need anything.`;
}
